export interface Product {
  id: number;
  product_name: string;
  product_desc: string;
  selling_price: number;
  cost_price?: number;
  discount?: number;
  quantity: number;
  category_id: number;
  frame_type_id?: number;
  frame_shape_id?: number;
  ideal_for?: number;
  brand?: string;
  model_no?: string;
  product_attr?: any;
  image_uri: string[] | null;
  /** True when the product has a genuine front/main image (type === 'main').
   * Virtual try-on needs a front shot to overlay, so side/folded-only
   * products are excluded (see lib/tryon/frameUtils.canTryOn). */
  hasMainImage?: boolean;
  /** The admin panel's cutout: the frame on transparency, made for wearing on a face.
   * Null when nobody has enhanced this product yet, in which case try-on falls back to the
   * catalogue photograph — which works, but carries whatever background it was shot on. */
  tryonImageUrl?: string | null;
  /** Whether this frame is offered for virtual try-on. Set per product in the admin panel
   * and returned by every product query. Opt-in: absent or false means no try-on, so a
   * response from an older API hides the button rather than showing one that misleads. */
  tryon_enabled?: boolean;
  is_wishlisted?: boolean;
  rating?: number;
  colour?: string;
  size?: string;
  status_id?: number;
  total_quantity_sold?: number;
}

/** Live price and stock for one product (GET /products/{id}/pricing). Product details are cached
 *  for the session; this is fetched fresh every time a price is shown. */
export interface VariantPricing {
  stock_id: number;
  selling_price: number;
  discount: number;
  quantity: number;
}

export interface ProductPricing {
  product_id: number;
  selling_price: number;
  discount: number;
  quantity: number;
  variants: VariantPricing[];
}

export interface CartItem {
  id: string;
  product_id: number;
  quantity: number;
  product_name: string;
  product_desc?: string;
  brand?: string;
  selling_price: number;
  discount?: number;
  available_quantity?: number;
  image_uri: string[] | null;
  category_id?: number;
  size?: string;
  product_attr?: any;
}

export interface WishlistItem {
  wishlist_id: string;
  product_id: number;
  product_name: string;
  selling_price: number;
  discount?: number;
  images?: string[] | null;
  brand?: string;
  category_id?: number;
}

export interface FilterParams {
  categoryIds?: number[];
  frameTypeIds?: number[];
  frameShapeIds?: number[];
  idealFor?: number[];
  productIds?: number[];
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
  searchText?: string;
  limit?: number;
  offset?: number;
}

export const CATEGORIES: Record<number, string> = {
  1: 'Eyeglasses',
  2: 'Lens',
  3: 'Sunglasses',
  4: 'Contact Lenses',
  5: 'Accessories',
  6: 'Blue Light Glasses',
  9: 'Reading Glasses',
};

export const FRAME_TYPES: Record<number, string> = {
  1: 'Full Rim',
  2: 'Half Rim',
  3: 'Rim Less',
  4: 'Supra',
};

// Ids must match the backend's frame_shape_id values — the try-on face-shape
// recommender maps face shapes onto these ids (see lib/tryon/faceShape.ts).
export const FRAME_SHAPES: Record<number, string> = {
  1: 'Rectangle',
  2: 'Round',
  3: 'Square',
  4: 'Wayfarer',
  5: 'Cat Eye',
  6: 'Aviator',
  7: 'Geometric',
  8: 'Hexagonal',
  9: 'Oval',
  10: 'Clubmaster',
  11: 'Sports',
  12: 'Octagon',
};

export const IDEAL_FOR: Record<number, string> = {
  1: 'Male',
  2: 'Female',
  3: 'Kids',
  4: 'Unisex',
};
